import classNames from 'clsx';
import React, { type ReactElement, memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DOCUMENTS_UNIQUE_IDS_PUBLICS } from 'reactApp/appHelpers/featuresHelpers/features/documentsUniqueIds';
import { SharingOnboarding } from 'reactApp/components/SharingWindow/blocks/SharingOnboarding/SharingOnboarding';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { useComponentSharing } from 'reactApp/components/SharingWindow/hooks/componentSharing.hook';
import { useOnboardingEnabled } from 'reactApp/components/SharingWindow/hooks/onboardingEnabled.hook';
import { HOT_KEY_SCOPE_NAME } from 'reactApp/components/SharingWindow/Sharing.constants';
import type { SharingContentProps } from 'reactApp/components/SharingWindow/Sharing.types';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { useIsWindowIntegrationSmall } from 'reactApp/hooks/useIsWindowIntegrationSmall';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { isDocument } from 'reactApp/modules/file/utils';
import { getPublicItemUniqueId } from 'reactApp/modules/modifying/modifying.actions';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { getItemById, getStorageItemById, isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendXray } from 'reactApp/utils/ga';

import styles from './Sharing.css';

export const SharingNewDesktop = memo(function SharingNewDesktop(props: IPropsWithPopup<SharingContentProps>): ReactElement {
    const { itemStorage, id, from, isPhone } = props;

    const dispatch = useDispatch();

    const isWindowSmall = useIsWindowIntegrationSmall();

    const { SharingContentComponent, sharingVariant } = useComponentSharing({ id, itemStorage });

    const item = useSelector((state: RootState) => {
        return itemStorage ? getStorageItemById(state, itemStorage, id) : getItemById(state, id);
    });

    const { isMounted } = useSelector((state: RootState) => isMountedOrSharedFolder(state, item));

    const isBiz = useSelector(UserSelectors.isBizUser);
    const isAlbumItem = isAlbum(item);
    const isFolder = item?.isFolder;

    const { onboardingFreeUsers } = useOnboardingEnabled(true);

    useEffect(() => {
        let type = 'file';
        const userType = isBiz ? 'biz' : 'b2c';

        if (isAlbumItem) {
            type = 'album';
        } else if (isFolder) {
            type = 'folder';
        }

        sendXray(['sharing-modal-desktop-mount', type, userType], isMounted ? { common: 1 } : undefined);

        if (DOCUMENTS_UNIQUE_IDS_PUBLICS && item && item.weblink && isDocument(item)) {
            dispatch(getPublicItemUniqueId({ id: item.id, weblink: item.weblink }));
        }
    }, []);

    return (
        <Dialog
            open
            id="sharing-new"
            onCancel={props.onClose}
            className={classNames(styles.modal, {
                [styles.tutoria]: isWindowSmall,
                [styles.sharingTabs]: sharingVariant === SHARING_VARIANT.folders,
                [styles.sharingRedesign]: sharingVariant === SHARING_VARIANT.files,
            })}
            bottomContent={
                onboardingFreeUsers && isFolder ? (
                    <SharingOnboarding
                        isPhone={isPhone}
                        from={from}
                        id={id}
                        itemStorage={itemStorage}
                        isTabDesign={sharingVariant === SHARING_VARIANT.folders}
                    />
                ) : null
            }
        >
            <Content isModal>
                <SharingContentComponent onboardingEnabled {...{ ...props, variant: sharingVariant }} />
            </Content>
        </Dialog>
    );
});

export const SharingNewPhone = memo(function SharingNewPhone(props: IPropsWithPopup<SharingContentProps>): ReactElement {
    const scrollRef = useRef(null);

    const { SharingContentComponent } = useComponentSharing({ ...props, isPhone: true });

    return (
        <MobileDialog id="sharing-new" closeOnDimmerClick mod="base" topmost onClose={props.onClose} scrollRef={scrollRef}>
            <SharingContentComponent {...props} isPhone ref={scrollRef} />
        </MobileDialog>
    );
});

export const SharingNew = memo(function SharingNew(props: IPropsWithPopup<SharingContentProps>): ReactElement {
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    useHotKeyScope(HOT_KEY_SCOPE_NAME);

    return isPhone ? <SharingNewPhone {...props} /> : <SharingNewDesktop {...props} />;
});
